import { Injectable } from "@angular/core";
import { AppService } from "@irb/app.service";
import { UtilityService } from "@irb-common/services/utility.service";
import { IPortfolioResultSECRebal, IPortfolioResultLotRebalanceSummary, IPortfolioResultResponse, IPortfolioResultOrderTotal, IPortfolioResultRequest, IPortfolioResultACCRebal, IPortfolioResultUpdateShareQtyRequest, IPortfolioResultUpdateShareQtyConfiguration } from "@irb/irb-editors/editor-portfolio-result/editor-portfolio-result.model";
import { PrUtilityService } from "@irb/irb-editors/editor-portfolio-result/services/pr-utility.service";
import { PrFormatService } from "@irb/irb-editors/editor-portfolio-result/services/pr-format.service";
import { PrAllocationsService } from "@irb/irb-editors/editor-portfolio-result/services/pr-allocations.service";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class PrOrdersService {
  shareQtyUpdateInProgress: boolean = false;
  private isRebalanceService: boolean = false;
  isCashEqvEnabled: boolean = false;
  hasUserEdits: boolean = false;
  hasOrders: boolean = false;

  constructor(private _http: HttpClient, private as: AppService, private util: UtilityService, private prutil: PrUtilityService, private prfs: PrFormatService, private pralloc: PrAllocationsService) {
    const irbAppConfig = this.as.getItem('IRB_APP_CONFIG');
    this.isRebalanceService = irbAppConfig?.featureFlags?.ENABLE_REBALANCE_APIS || false;
    this.isCashEqvEnabled = irbAppConfig?.featureFlags?.isCashEqvEnabled || false;
  }

  resetSecurity(item: IPortfolioResultSECRebal) {
    item.txnAmt = 0;
    item.txnAmtEdit = !(item.secType === "OPTION"); // Always false if it is option
    item.txnShares = 0;
    item.shareQtyEdit = true;
    item.txnSharesLocked = false;
    item.sellTerm = "";
    item.penaltyAmt = 0;
    item.realzdGainLoss = 0;
  }

  resetSecLots(item: IPortfolioResultSECRebal) {
    if (item.lotRebalanceSummaryDTOList) {
      this.resetLots(item.lotRebalanceSummaryDTOList);
    }

    item.sellTerm = "";
    item.penaltyAmt = 0;
    item.realzdGainLoss = 0;
  }

  resetLots(items: Array<IPortfolioResultLotRebalanceSummary>) {
    items.forEach((item: IPortfolioResultLotRebalanceSummary) => {
      this.resetLot(item);
    });
  }

  resetLot(item: IPortfolioResultLotRebalanceSummary) {
    item.txnAmt = 0;
    item.txnShares = 0;
    item.txnSharesLocked = false;
    item.txnType = "NONE";
    item.sellTerm = "";
    item.penaltyAmt = 0;
    item.realzdGainLoss = 0;
    item.parentSellAll = false;
  }

  updateLotAndParentSec(data: Array<IPortfolioResultSECRebal>, item: IPortfolioResultLotRebalanceSummary, cbFunc: Function) {
    const parentItem = this.getParentItem(data, item.parentId);
    this.updateLotItem(item, parentItem.accountType);
    this.updateSecItem(parentItem, cbFunc);
  }

  updateLotItem(item: IPortfolioResultLotRebalanceSummary, accType: string) {
    if (item.txnAmt !== 0 && accType === "T") {
      //IRE096.3 : sell term for taxable acc only.
      if (item.shortTerm) {
        item.sellTerm = "ST";
      } else {
        item.sellTerm = "LT";
      }
    } else {
      item.sellTerm = "";
    }
  }

  updateSecItem(item: IPortfolioResultSECRebal, cbFunc: Function) {
    let secTxnAmt = 0;
    let secTxnShares = 0;
    let isSTLot = false;
    let isLTLot = false;
    let secTxnIsNone = true;
    let soldAllLots = true;
    const isOption = "OPTION" === item.secType;
    let _lotTxnType;

    item.lotRebalanceSummaryDTOList.forEach((lotItem: IPortfolioResultLotRebalanceSummary) => {
      secTxnAmt += lotItem.txnAmt;
      secTxnShares += lotItem.txnShares;

      if (lotItem.sellTerm === "ST") {
        isSTLot = true;
      } else if (lotItem.sellTerm === "LT") {
        isLTLot = true;
      }

      if (lotItem.txnType !== "NONE") {
        secTxnIsNone = false;
        _lotTxnType = lotItem.txnType;
      }

      if (lotItem.txnAmt !== lotItem.roundedMktValue) {
        soldAllLots = false;
      }
    });

    if (secTxnIsNone) {
      item.txnType = "NONE";
      item.txnAmt = 0;
      item.txnShares = 0;
    } else if (soldAllLots && !isOption) {
      item.txnType = "SELL ALL";
      item.txnAmt = item.roundedMktValue;
      item.txnShares = item.txnShares;
      this.setLotsFlagForPosSellAll(item, true);

      if ((item.txnType !== "SELL" && item.txnType !== "SELL ALL") || item.roundedMktValue > item.iRblTxnAmt) {
        cbFunc();
      }
    } else {
      if (!isOption) {
        item.txnType = "SELL";
      } else {
        item.txnType = _lotTxnType;
      }

      item.txnAmt = this.prutil.roundDecNumber(secTxnAmt, 2);
      item.txnShares = secTxnShares;
      this.setLotsFlagForPosSellAll(item, false);

      if ((item.txnType !== "SELL" && item.txnType !== "SELL ALL") || secTxnAmt > item.iRblTxnAmt) {
        cbFunc();
      }
    }

    item.txnTypeEdit = true;
    item.txnAmtEdit = !isOption; // Always false if it is options
    item.shareQtyEdit = true;
    this.calculateSecPenalty(item);
    this.setSecuritySellTerm(isSTLot, isLTLot, item);
  }

  setLotsFlagForPosSellAll(item: IPortfolioResultSECRebal, isSellAll: boolean) {
    item.lotRebalanceSummaryDTOList.forEach((lotItem: IPortfolioResultLotRebalanceSummary) => {
      lotItem.parentSellAll = isSellAll;
    });
  }

  calculateSecPenalty(item: IPortfolioResultSECRebal) {
    const txnAmount = this.getTxnAmt(item);

    let mfPenalty = 0;
    let tdaPenalty = 0;
    let netPenalty = 0;
    const isSellType = this.getSellType(item.txnType);

    if (txnAmount !== 0) {
      if (item.etfCommission) {
        item.penaltyAmt = item.etfCommissionAmt;
        return;
      }

      if (item.secType === "MF" && txnAmount > item.mfPenaltyFreeSellAmt && isSellType) {
        const sellAmtForPenalty = txnAmount - item.mfPenaltyFreeSellAmt;
        mfPenalty = this.getPenaltyAmount(0, item.redemPenaltyPercnt, sellAmtForPenalty);
      }

      if (item.secType === "MF" && txnAmount > item.tdaPenaltyFreeSellAmt && isSellType) {
        tdaPenalty = item.redemPenaltyAmt;
      }

      netPenalty = mfPenalty + tdaPenalty;

      item.penaltyAmt = this.prutil.roundDecNumber(netPenalty, 5);
    } else {
      item.penaltyAmt = 0;
    }
  }

  getPenaltyAmount(redemPenaltyAmt: number, redemPenaltyPercnt: number, txnAmount: number): number {
    if (redemPenaltyAmt !== 0) {
      return redemPenaltyAmt;
    }

    if (redemPenaltyPercnt !== 0) {
      return (txnAmount * redemPenaltyPercnt) / 100;
    }
  }

  setSecuritySellTerm(isSTLot: boolean, isLTLot: boolean, item: IPortfolioResultSECRebal) {
    if (isLTLot && !isSTLot) {
      item.sellTerm = "LT";
    } else if (isSTLot && !isLTLot) {
      item.sellTerm = "ST";
    } else if (isLTLot && isSTLot) {
      item.sellTerm = "MIX";
    } else {
      item.sellTerm = "";
    }
  }

  setIsUserTxnComplete(item: IPortfolioResultSECRebal) {
    item.userTxnComplete = item.txnType === "SELL ALL" || item.txnType === "NONE";
  }

  setTxnCurrency(item: IPortfolioResultSECRebal, columnCurrency: string) {
    if (item.txnType !== "NONE") {
      if (item.secType === "OPTION") {
        item.txnCurrency = "SHARES";
      } else {
        item.txnCurrency = columnCurrency;
      }
    }
  }

  calBasedonPOValChange(data: IPortfolioResultResponse, item: IPortfolioResultSECRebal, forceReset?: boolean) {
    const secType = item.secType;
    const mktValueCurrent = item.mktValue;
    const sharesCurrent = item.currentShares;
    const txnTypeCurrent = item.txnType;

    if (secType === "OTHER" || secType === "GROUP" || secType === "BOND") {
      const txnAmtCurrent = this.getTxnAmt(item);

      if (txnTypeCurrent === "BUY") {
        item.afterRebalMktVal = this.prutil.roundDecNumber(mktValueCurrent + txnAmtCurrent, 5);
      } else if (txnTypeCurrent === "SELL" || txnTypeCurrent === "SELL ALL") {
        item.afterRebalMktVal = this.prutil.roundDecNumber(mktValueCurrent - txnAmtCurrent, 5);
      } else {
        item.afterRebalMktVal = mktValueCurrent;
      }

      this.updateLotAndParentAfterRebalValues(item);
    } else if (forceReset) {
      item.afterRebalMktVal = mktValueCurrent;
      item.afterRebalShares = sharesCurrent;
      this.updateLotAndParentAfterRebalValues(item);
    }

    this.updateLotAndParentSecRelGainLoss(item);
    let dollarCashAdjAmt = 0;

    const cashAdj = {
      totalBuy: 0,
      totalSell: 0,
    };
    const equivalentsAdj = {
      totalBuy: 0,
      totalSell: 0,
    };
    // Order Totals
    const orderTotalsVal = {
      buysQty: 0,
      buysValue: 0,
      sellsQty: 0,
      sellsValue: 0,
      stQty: 0,
      stValue: 0,
      ltQty: 0,
      ltValue: 0,
      totPenalitiesQty: 0,
      totPenalitiesValue: 0,
    };

    const securityVal = {
      totalBuy: 0,
      totalSell: 0,
    };

    let checkAllTxnTypeRNone = true;
    const rebalSecReportList = data.rebalSecReportList;
    data.secRebalDTOList.forEach((orderItem: IPortfolioResultSECRebal) => {
      if(orderItem.rowId === item.rowId) {
        orderItem = item;
      }

      const txnType = orderItem.txnType;
      const txnAmt = this.getTxnAmt(orderItem);
      const accountNum = orderItem.accountNum;
      const penaltyAmt = orderItem.penaltyAmt;
      const securityId = orderItem.securityId;
      const secSymbol = orderItem.secSymbol;
      const isEquivalent = this.isEquivalent(rebalSecReportList, secSymbol);
      if ((txnType === "BUY" || txnType === "BUY_TO_OPEN" || txnType === "BUY_TO_CLOSE") && txnAmt !== 0) {
        orderTotalsVal.buysQty++;
        orderTotalsVal.buysValue = orderTotalsVal.buysValue + txnAmt;
        if (item.accountNum === accountNum) {
          cashAdj.totalBuy = cashAdj.totalBuy + txnAmt;
          if (isEquivalent) {
            equivalentsAdj.totalBuy = equivalentsAdj.totalBuy - txnAmt;
          }
        }

        if (item.securityId === securityId) {
          securityVal.totalBuy = securityVal.totalBuy + txnAmt;
        }

        checkAllTxnTypeRNone = false;
        dollarCashAdjAmt = dollarCashAdjAmt - txnAmt; //#56747
      } else if ((txnType === "SELL" || txnType === "SELL_TO_OPEN" || txnType === "SELL_TO_CLOSE" || txnType === "SELL ALL") && txnAmt !== 0) {
        orderTotalsVal.sellsQty++;
        orderTotalsVal.sellsValue = orderTotalsVal.sellsValue + txnAmt;
        if (item.accountNum === accountNum) {
          cashAdj.totalSell = cashAdj.totalSell + txnAmt;
          if (isEquivalent) {
            equivalentsAdj.totalSell = equivalentsAdj.totalSell - txnAmt;
          }
        }

        if (item.securityId === securityId) {
          securityVal.totalSell = securityVal.totalSell + txnAmt;
        }

        checkAllTxnTypeRNone = false;
        dollarCashAdjAmt = dollarCashAdjAmt + txnAmt;
      }

      // Total Penalities
      if (penaltyAmt > 0) {
        orderTotalsVal.totPenalitiesQty++;
        orderTotalsVal.totPenalitiesValue = orderTotalsVal.totPenalitiesValue + penaltyAmt;
      }

      this.getShortLongRelGainLoss(orderItem, orderTotalsVal);
    });

    //orderTotalsVal.totPenalitiesValue = this.prfs.formatRounding(Number(orderTotalsVal.totPenalitiesValue), 2);
    orderTotalsVal.totPenalitiesValue = this.prutil.roundDecNumber(orderTotalsVal.totPenalitiesValue, 2);

    this.calOrderTotalsVal(data, orderTotalsVal);
    this.calCashNeedsAdj(data.accRebalDTOList, item.accountNum, cashAdj.totalSell - cashAdj.totalBuy, equivalentsAdj.totalSell - equivalentsAdj.totalBuy);
    this.calAllocationsAdj(data, item.securityId, securityVal.totalBuy - securityVal.totalSell, dollarCashAdjAmt);

    data.userRebalStatus = checkAllTxnTypeRNone ? 'NA' : 'PENDING';
  }

  calCashNeedsAdj(data: Array<IPortfolioResultACCRebal>, accountNumSelected: string, cashAdjAmt: number, equivalentsAdjAmt: number) {
    data.forEach((item: IPortfolioResultACCRebal) => {
      if (item.accountNum === accountNumSelected) {
        item.cashAdj = cashAdjAmt;
        item.cashEquivAdj = equivalentsAdjAmt;
        item.afterRebalTotalCash = this.prutil.roundDecNumber(item.totalCash + cashAdjAmt, 5);
        item.afterRebalAvailAmt = this.prutil.roundDecNumber(item.availCash + cashAdjAmt, 5);
        item.cashEquivAfterAdj = this.prutil.roundDecNumber((item.cashEquivAvail ?? 0) + equivalentsAdjAmt, 5);
      }
    });
  }

  calAllocationsAdj(data: IPortfolioResultResponse, secId: number, pstAdjAmt: number, cashAdjAmt: number) {
    setTimeout(() => {
      this.pralloc.updateSecInAllocation(data, secId, pstAdjAmt);
      this.pralloc.updateSecInAllocation(data, null, cashAdjAmt);
      this.pralloc.updateAfterAdjValues(data);
    });
  }

  getTxnAmt(item: any) {
    let txnAmt = item.txnAmt;

    if (item.txnAmt !== item.roundedIRblTxnAmt) {
      if (item.txnAmt === item.roundedMktValue) {
        txnAmt = item.mktValue;
      }
    } else {
      txnAmt = item.iRblTxnAmt;
    }

    return txnAmt;
  }

  updateLotAndParentAfterRebalValues(item: IPortfolioResultSECRebal) {
    let mktValueTotal = 0;
    let sharesTotal = 0;

    item.lotRebalanceSummaryDTOList.forEach((singleLot: IPortfolioResultLotRebalanceSummary) => {
      const lotMktValueCurrent = singleLot.mktValue;
      const lotTxnAmtCurrent = singleLot.txnAmt;
      const lotTxnSharesCurrent = singleLot.txnShares;
      const lotSharesCurrent = singleLot.currentShares;
      const multiplier = lotSharesCurrent < 0 ? -1 : 1;
      const txnType = singleLot.txnType;
      const isSellType = this.getSellType(txnType);
      const isBuyType = this.getBuyType(txnType);

      if (isSellType) {
        let m = multiplier * lotMktValueCurrent - lotTxnAmtCurrent;
        singleLot.afterRebalMktVal = m;
        mktValueTotal += m;

        m = multiplier * lotSharesCurrent - lotTxnSharesCurrent;
        singleLot.afterRebalShares = m;
        sharesTotal += m;
      } else if (isBuyType) {
        let m = multiplier * lotMktValueCurrent + lotTxnAmtCurrent;
        singleLot.afterRebalMktVal = m;
        mktValueTotal += m;

        m = multiplier * lotSharesCurrent + lotTxnSharesCurrent;
        singleLot.afterRebalShares = m;
        sharesTotal += m;
      } else {
        singleLot.afterRebalMktVal = lotMktValueCurrent;
        mktValueTotal += lotMktValueCurrent;

        singleLot.afterRebalShares = lotSharesCurrent;
        sharesTotal += lotSharesCurrent;
      }
    });

    item.afterRebalMktVal = mktValueTotal;
    item.afterRebalShares = sharesTotal;
  }

  updateLotAndParentSecRelGainLoss(item: IPortfolioResultSECRebal) {
    const accType = item.accountType;
    let secRelGainLoss = 0;

    item.lotRebalanceSummaryDTOList.forEach((singleLot: IPortfolioResultLotRebalanceSummary) => {
      const lotMktValueCurrent = singleLot.mktValue;
      const lotTxnAmtCurrent = this.getTxnAmt(singleLot);

      if (accType === "T") {
        const sharesCurrent = item.currentShares;
        const multiplier = sharesCurrent < 0 ? -1 : 1;
        const lotUnRelGainLoss = singleLot.unrealzdGainOrLoss;
        const lotRelGainLoss = this.prutil.roundDecNumber(multiplier * ((lotUnRelGainLoss * lotTxnAmtCurrent) / lotMktValueCurrent), 5);
        singleLot.realzdGainLoss = lotRelGainLoss;
        secRelGainLoss = secRelGainLoss + lotRelGainLoss;
      }
    });

    item.realzdGainLoss = this.prutil.roundDecNumber(secRelGainLoss, 5);
  }

  getShortLongRelGainLoss(item: IPortfolioResultSECRebal, orderTotalsVal: any) {
    item.lotRebalanceSummaryDTOList.forEach((singleLot: IPortfolioResultLotRebalanceSummary) => {
      const sellTerm = singleLot.sellTerm;
      const relGainLoss = singleLot.realzdGainLoss;

      if (sellTerm === "ST") {
        orderTotalsVal.stValue = orderTotalsVal.stValue + relGainLoss;
      } else if (sellTerm === "LT") {
        orderTotalsVal.ltValue = orderTotalsVal.ltValue + relGainLoss;
      }
    });
  }

  calOrderTotalsVal(data: IPortfolioResultResponse, orderTotalsVal: any) {
    data.orderTotalDTOList.forEach((item: IPortfolioResultOrderTotal) => {
      const rowHdrStr = item.rowHdrStr;

      if (rowHdrStr === "Buys") {
        item.userQuantity = orderTotalsVal.buysQty;
        item.userTotValue = this.prutil.roundDecNumber(orderTotalsVal.buysValue, 5);
      } else if (rowHdrStr === "Sells") {
        item.userQuantity = orderTotalsVal.sellsQty;
        item.userTotValue = this.prutil.roundDecNumber(orderTotalsVal.sellsValue, 5);
      } else if (rowHdrStr === "Total Trades") {
        if (!orderTotalsVal.sellsValue) {
          orderTotalsVal.sellsValue = 0;
        }
        item.userQuantity = orderTotalsVal.buysQty + orderTotalsVal.sellsQty;
        item.userTotValue = this.prutil.roundDecNumber(orderTotalsVal.buysValue + orderTotalsVal.sellsValue, 5);
      } else if (rowHdrStr === "Penalties") {
        item.userQuantity = orderTotalsVal.totPenalitiesQty;
        item.userTotValue = this.prutil.roundDecNumber(orderTotalsVal.totPenalitiesValue, 5);
      } else if (rowHdrStr === "Short-term") {
        item.userQuantity = -999;
        item.userTotValue = this.prutil.roundDecNumber(orderTotalsVal.stValue, 5);
      } else if (rowHdrStr === "Long-term") {
        item.userQuantity = -999;
        item.userTotValue = this.prutil.roundDecNumber(orderTotalsVal.ltValue, 5);
      } else if (rowHdrStr === "Total gain/loss (estimated)") {
        item.userQuantity = -999;
        item.userTotValue = this.prutil.roundDecNumber(orderTotalsVal.stValue + orderTotalsVal.ltValue, 5);
      }
    });
  }

  getParentItem(data: Array<IPortfolioResultSECRebal>, parentId: number): IPortfolioResultSECRebal {
    let a: IPortfolioResultSECRebal = null;

    data.forEach((item: IPortfolioResultSECRebal) => {
      if (item.rowId === parentId) {
        a = item;
      }
    });

    return a;
  }

  getLotItem(data: Array<IPortfolioResultLotRebalanceSummary>, rowId: number): IPortfolioResultLotRebalanceSummary {
    let a: IPortfolioResultSECRebal = null;

    data.forEach((item: IPortfolioResultLotRebalanceSummary) => {
      if (item.rowId === rowId) {
        a = item;
      }
    });

    return a;
  }

  getSellType(txnType: string): boolean {
    return txnType === "SELL" || txnType === "SELL ALL" || txnType === "SELL_TO_OPEN" || txnType === "SELL_TO_CLOSE";
  }

  getBuyType(txnType: string): boolean {
    return txnType === "BUY" || txnType === "BUY_TO_OPEN" || txnType === "BUY_TO_CLOSE";
  }

  getCloseType(txnType: string): boolean {
    return txnType === "SELL_TO_CLOSE" || txnType === "BUY_TO_CLOSE";
  }

  getAfterRebalCashAmount(data: IPortfolioResultResponse, accountNum: string, availCash: number, cbFunc: Function) {
    let c = 0;

    data.secRebalDTOList.forEach((order: IPortfolioResultSECRebal) => {
      if (order.accountNum === accountNum) {
        const isSellType = this.getSellType(order.txnType);
        const txnAmt = this.getTxnAmt(order);

        if (isSellType) {
          c += txnAmt;
        } else {
          c -= txnAmt;
        }
      }
    });

    cbFunc(availCash + c);
  }

  distributeSellInLots(item: IPortfolioResultSECRebal) {
    const txnAmount = this.getTxnAmt(item);
    const isOption = item.secType === "OPTION";
    let netTxnAmt = txnAmount;
    let isSTLot = false;
    let isLTLot = false;

    if (item.txnType === "SELL") {
      this.checkSecIsSellAll(item);
    }

    const isSecSellALL = item.txnType === "SELL ALL" ? true : false;

    item.lotRebalanceSummaryDTOList.forEach((singleLot: IPortfolioResultLotRebalanceSummary) => {
      this.resetLot(singleLot);
      const mktValue = singleLot.mktValue;
      const roundedMktVal = singleLot.roundedMktValue;
      const shortTerm = singleLot.shortTerm;
      const isShortPosition = mktValue < 0;

      // Set txnAmt
      if ((isShortPosition && netTxnAmt >= -1 * mktValue) || (!isShortPosition && netTxnAmt >= mktValue)) {
        singleLot.txnAmt = isShortPosition ? roundedMktVal * -1 : roundedMktVal;

        if (!isOption) {
          singleLot.txnType = "SELL"; //valid options are sell and none only for lot
        } else {
          singleLot.txnType = item.txnType;
        }

        if (item.accountType === "T") {
          if (shortTerm) {
            singleLot.sellTerm = "ST";
            isSTLot = true;
          } else {
            singleLot.sellTerm = "LT";
            isLTLot = true;
          }
        }

        netTxnAmt = netTxnAmt - mktValue;
      } else if (netTxnAmt !== 0) {
        if (isSecSellALL) {
          singleLot.txnAmt = roundedMktVal;
        } else {
          singleLot.txnAmt = this.prutil.roundDecNumber(netTxnAmt, 2);
        }

        if (!isOption) {
          singleLot.txnType = "SELL"; //valid options are sell and none only for lot
        } else {
          singleLot.txnType = item.txnType;
        }

        if (item.accountType === "T") {
          if (shortTerm) {
            singleLot.sellTerm = "ST";
            isSTLot = true;
          } else {
            singleLot.sellTerm = "LT";
            isLTLot = true;
          }
        }

        netTxnAmt = 0;
      }
    });

    this.calculateSecPenalty(item);

    if (item.accountType === "T") {
      this.setSecuritySellTerm(isSTLot, isLTLot, item);
    }
  }

  distributeSharesInLots(item: IPortfolioResultSECRebal) {
    const isOption = item.secType === "OPTION";
    let netTxnShares = item.txnShares;
    let isSTLot = false;
    let isLTLot = false;

    if (item.txnType === "SELL") {
      this.checkSecIsSellAll(item);
    }

    const isSecSellALL = item.txnType === "SELL ALL" ? true : false;

    item.lotRebalanceSummaryDTOList.forEach((singleLot: IPortfolioResultLotRebalanceSummary) => {
      this.resetLot(singleLot);
      const mktValue = singleLot.mktValue;
      const currentShares = singleLot.currentShares;
      const roundedMktVal = singleLot.roundedMktValue;
      const shortTerm = singleLot.shortTerm;
      const isShortPosition = mktValue < 0;

      // Set txnShares
      if ((isShortPosition && netTxnShares >= -1 * currentShares) || (!isShortPosition && netTxnShares >= currentShares)) {
        singleLot.txnShares = isShortPosition ? currentShares * -1 : currentShares;

        if (!isOption) {
          singleLot.txnType = "SELL"; //valid options are sell and none only for lot
        } else {
          singleLot.txnType = item.txnType;
        }

        if (item.accountType === "T") {
          if (shortTerm) {
            singleLot.sellTerm = "ST";
            isSTLot = true;
          } else {
            singleLot.sellTerm = "LT";
            isLTLot = true;
          }
        }

        if (isShortPosition) {
          netTxnShares = netTxnShares - -1 * currentShares;
        } else {
          netTxnShares = netTxnShares - currentShares;
        }
      } else if (netTxnShares !== 0) {
        if (isSecSellALL) {
          singleLot.txnShares = currentShares;
        } else {
          singleLot.txnShares = netTxnShares;
        }

        if (!isOption) {
          singleLot.txnType = "SELL"; //valid options are sell and none only for lot
        } else {
          singleLot.txnType = item.txnType;
        }

        if (item.accountType === "T") {
          //IRE096.3 : sell term for taxable acc only.
          if (shortTerm) {
            singleLot.sellTerm = "ST";
            isSTLot = true;
          } else {
            singleLot.sellTerm = "LT";
            isLTLot = true;
          }
        }

        netTxnShares = 0;
      }
    });

    this.calculateSecPenalty(item);

    if (item.accountType === "T") {
      this.setSecuritySellTerm(isSTLot, isLTLot, item);
    }
  }

  checkSecIsSellAll(item: IPortfolioResultSECRebal) {
    const secTxnAmt = this.getTxnAmt(item);

    item.txnShares = item.txnShares === null ? 0 : item.txnShares;
    item.currentShares = item.currentShares === null ? 0 : item.currentShares;

    if ((secTxnAmt !== 0 && secTxnAmt >= item.mktValue) || (item.txnShares !== 0 && item.txnShares >= item.currentShares)) {
      item.txnType = "SELL ALL";
      this.setLotsFlagForPosSellAll(item, true);
    }
  }

  confirmTxnSharesEdit(config: IPortfolioResultUpdateShareQtyConfiguration, isSellAll: boolean) {
    if (isSellAll) {
      config.item.txnType = "SELL ALL";
      config.item.txnAmt = config.item.roundedMktValue;
      config.item.txnAmtEdit = true;

      this.distributeSellInLots(config.item);
      
      config.isShareQtyEdit = false;
      config.editField = "txnAmt";
      
      this.updateShareQty(config);
    }
  }

  updateShareQty(config: IPortfolioResultUpdateShareQtyConfiguration) {
    let shareRound = config.data.envGlobalSettingsDTO.shareRoundingSetting;
    const reqFields = ["rebalSecId", "securityId", "ordHarvestedSecId", "secDeleted", "secAccId", "editedQuantity", "secSymbol", "secType", "accountNum", "accountType", "txnType", "txnAmt", "txnShares", "currentShares", "txnSharesLocked", "shareQtyEdit", "txnAmtEdit", "afterRebalMktVal", "afterRebalShares", "realzdGainLoss", "penaltyAmt", "sellTerm", "washSell", "washSaleLotSoldAtLoss", "quantity", "mktValue", "lotRebalanceSummaryDTOList", "lotHoldingsId", "rebalLotId", "capGainsReinvest", "dividendReinvest", "accountLocationType", "swapExchange", "swapExchangeMapId", "swapExchangeTxnType", "currMarketPrice", "ccDNSRestriction", "secAnalysisPrice", "iRblTxnAmt"];
    const txnType = config.item.txnType;
    const isSellType = this.getSellType(txnType);
    const isCloseType = this.getCloseType(txnType);

    if (config.item.secType === "GROUP") {
      return;
    }

    if ((isSellType || isCloseType) && txnType !== "SELL_TO_OPEN" && !config.initiatedByLotEdit) {
      if (!config.isShareQtyEdit) {
        this.distributeSellInLots(config.item);
      } else {
        if (!isCloseType && config.item.txnShares === config.item.currentShares) {
          this.confirmTxnSharesEdit(config, true);
          return;
        }

        this.distributeSharesInLots(config.item);
      }
    }

    if (config.editField === "txnAmt" && !config.isShareQtyEdit) {
      const txnAmtEdit = config.item.txnAmt !== config.previousTxnAmt;
      config.item.txnAmtEdit = txnAmtEdit;
      config.item.shareQtyEdit = false;

      if (config.item.txnAmtEdit) {
        //Removing the lock on edit of order amount
        config.item.txnSharesLocked = false;
        config.item.shareQtyEdit = false;
        config.item.shareQtyEdit = false;
        config.item.txnAmtEdit = true;
        config.item.txnSharesLocked = false;

        for(let i = 0; i < config.item.lotRebalanceSummaryDTOList.length; i++) {
          config.item.lotRebalanceSummaryDTOList[i].shareQtyEdit = false;
          config.item.lotRebalanceSummaryDTOList[i].txnAmtEdit = true;
        }
      }
    } else if (config.editField === "txnShares" && config.isShareQtyEdit) {
      shareRound = "";
      config.item.txnSharesLocked = true;
      config.item.shareQtyEdit = true;
      config.item.txnAmtEdit = false;
      config.item.shareQtyEdit = true;
      config.item.txnAmtEdit = false;
      config.item.txnSharesLocked = true;

      for(let i = 0; i < config.item.lotRebalanceSummaryDTOList.length; i++) {
        config.item.lotRebalanceSummaryDTOList[i].shareQtyEdit = true;
        config.item.lotRebalanceSummaryDTOList[i].txnAmtEdit = false;
      }
    }

    const _item = this.util.getObjectValues(config.item, reqFields);

    for(let i = 0; i < _item.lotRebalanceSummaryDTOList.length; i++) {
      _item.lotRebalanceSummaryDTOList[i] = this.util.getObjectValues(_item.lotRebalanceSummaryDTOList[i], reqFields);
    }

    const req = {
      shareRounding: shareRound,
      securityRebalance: _item,
      rebalStatus: config.data.rebalStatus,
      isMfSellAmtTypeDollars: !_item.txnSharesLocked
    } as IPortfolioResultUpdateShareQtyRequest;

    if ((config.editField === "txnAmt" && config.item.txnAmtEdit) || (config.editField === "txnShares" && config.item.shareQtyEdit)) {
      this.shareQtyUpdateInProgress = true;

      this.updateShareQtyForSecRebalanceSummary(req).subscribe((resp: IPortfolioResultSECRebal) => {
        config.item.editedQuantity = resp.editedQuantity;
        config.item.afterRebalShares = resp.afterRebalShares;
        config.item.afterRebalMktVal = resp.afterRebalMktVal;
        config.item.txnSharesLocked = resp.txnSharesLocked;
        config.item.txnAmt = resp.txnAmt;
        config.item.txnShares = resp.txnShares;
        config.item.txnEditIn = resp.txnEditIn ? resp.txnEditIn : config.item.txnEditIn;
        config.item.txnCurrency = resp.txnCurrency ? resp.txnCurrency : config.item.txnCurrency;

        //console.log("******** /update-share-qty-for-sec-rebalance-summary");
        //console.log("REQ", req.securityRebalance.txnAmt, req.securityRebalance.txnShares, req.securityRebalance.txnType);
        //console.log("RES", resp.txnAmt, resp.txnShares);

        const f = () => {
          for(let i = 0; i < config.item.lotRebalanceSummaryDTOList.length; i++) {
            const singleLot = config.item.lotRebalanceSummaryDTOList[i];
            singleLot.afterRebalShares = resp.lotRebalanceSummaryDTOList[i].afterRebalShares;
            singleLot.afterRebalMktVal = resp.lotRebalanceSummaryDTOList[i].afterRebalMktVal;
            singleLot.txnSharesLocked = resp.txnSharesLocked;
            singleLot.txnAmt = resp.lotRebalanceSummaryDTOList[i].txnAmt;
            singleLot.txnShares = resp.lotRebalanceSummaryDTOList[i].txnShares;

            //console.log("  REQ LOT", req.securityRebalance.lotRebalanceSummaryDTOList[i].txnAmt, req.securityRebalance.lotRebalanceSummaryDTOList[i].txnShares, req.securityRebalance.lotRebalanceSummaryDTOList[i].txnType);
            //console.log("  RES LOT", singleLot.txnAmt, singleLot.txnShares);
          }

          this.calBasedonPOValChange(config.data, config.item);

          if (config.onComplete) {
            config.onComplete(true);
          }
        }

        if (config.isShareQtyEdit) {
          if (isSellType) {
            this.calculateSecPenalty(config.item);
          }

          config.checkValidationFunction(config.data, config.item, "sellOrBuyMin", (callStack: Array<string>) => {
            config.checkValidationFunction(config.data, config.item, "equityTradeMinimum", (callStack: Array<string>) => {
                if (config.item.ccDNSRestriction && resp.txnAmt > config.item.iRblTxnAmt) {
                  config.checkValidationFunction(config.data,  config.item, "sellRestricn", () => {
                      f();
                    }, callStack);
                } else {
                  f();
                }
              }, callStack);
          });

          this.shareQtyUpdateInProgress = false;
        } else {
          if(config.checkValidationFunction) {
            config.checkValidationFunction(config.data, config.item, "fractionalShare", (isValid: boolean) => {
              if(isValid) {
                f();
              } else {
                if(config.onComplete) {
                  config.onComplete(false);
                }
              }
            });
          } else {
            f();
          }

          this.shareQtyUpdateInProgress = false;
        }
      }, () => { // revert in case of error
        config.item.txnType = config.snapShotItem.txnType;
        config.item.txnAmt = config.snapShotItem.txnAmt;
        config.item.txnShares = config.snapShotItem.txnShares;

        for(let i = 0; i < config.item.lotRebalanceSummaryDTOList.length; i++) {
          const singleLot = config.item.lotRebalanceSummaryDTOList[i];
          singleLot.txnType = config.snapShotItem.lotRebalanceSummaryDTOList[i].txnType;
          singleLot.txnAmt = config.snapShotItem.lotRebalanceSummaryDTOList[i].txnAmt;
          singleLot.txnShares = config.snapShotItem.lotRebalanceSummaryDTOList[i].txnShares;
        }

        if (config.onComplete) {
          config.onComplete(true);
        }

        this.shareQtyUpdateInProgress = false;
      });
    }
  }

  updateShareQtyForSecRebalanceSummary(request: IPortfolioResultUpdateShareQtyRequest): Observable<Object> {
    return this._http.post("/irebalone/update-share-qty-for-sec-rebalance-summary", request, {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
        Expires: "Sat, 01 Jan 2000 00:00:00 GMT",
      }),
    });
  }

  checkForUserEdits(data: IPortfolioResultResponse) {
    this.hasUserEdits = false;

    if(data.secRebalDTOList && typeof data.secRebalDTOList === 'object') {
      for(const orderItem of data.secRebalDTOList) {
          let a = orderItem.txnType !== orderItem.iRblTxnType;
          let b = orderItem.txnAmt !== orderItem.roundedIRblTxnAmt;

          if(a || b) {
            this.hasUserEdits = a || b;
            break;
          }
      }
    }
  }

   isEquivalent(rebalSecReportList: Array<any> | null | undefined, securitySymbol: string): boolean {
     return this.isCashEqvEnabled && !!rebalSecReportList?.some(
       (item) => item.securitySymbol === securitySymbol && typeof item.category === 'string' && item.category.toLowerCase() === 'equivalent'
     );
   }

  checkForOrders(data: IPortfolioResultResponse) {
    this.hasOrders = false;

    if(data.secRebalDTOList && typeof data.secRebalDTOList === 'object') {
      for(const orderItem of data.secRebalDTOList) {
          let a = orderItem.txnType !== "NONE";

          if(a) {
            this.hasOrders = a;
            break;
          }
      }
    }
  }
}
